/**
 * PortalReminderModal — patient-portal popup for due follow-up reminders
 * (Phase 5e, Jun 11 2026 — Ana's Operations workflow spec).
 *
 * On mount, calls portal:reminders-due with the portal sessionToken
 * (token-gated server-side; scoped to the authenticated patient's own
 * patient_portal_popup rows). If any are due, renders a single modal that
 * walks the patient through each card with an acknowledge button that
 * fires portal:reminder-dismiss.
 *
 * Patient is authenticated via the portal credential model, so full
 * details render (per the spec's PHI note). Renders nothing when there's
 * nothing due or if the channel is unavailable.
 */
import React, { useState, useEffect, useCallback } from "react";

export default function PortalReminderModal({ sessionToken }) {
  const [reminders, setReminders] = useState([]);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!sessionToken) return;
      try {
        const r = await window.zignagenixAPI?.portalRemindersDue?.(sessionToken)
          || await window.zignagenixAPI?.invoke?.("portal:reminders-due", sessionToken);
        if (cancelled) return;
        if (r && !r.error && Array.isArray(r.reminders) && r.reminders.length) {
          setReminders(r.reminders);
        }
      } catch (_) { /* channel unavailable — silent no-op */ }
    }
    load();
    return () => { cancelled = true; };
  }, [sessionToken]);

  const acknowledge = useCallback(async () => {
    const current = reminders[idx];
    if (!current) return;
    setBusy(true);
    try {
      await (window.zignagenixAPI?.portalReminderDismiss?.(sessionToken, current.id)
        || window.zignagenixAPI?.invoke?.("portal:reminder-dismiss", sessionToken, current.id));
    } catch (_) { /* non-fatal — advance anyway */ } finally {
      setBusy(false);
      if (idx + 1 < reminders.length) setIdx(idx + 1);
      else setClosed(true);
    }
  }, [reminders, idx, sessionToken]);

  if (closed || !reminders.length) return null;
  const current = reminders[idx];
  const card = current?.card || {};

  return (
    <div role="dialog" aria-modal="true" aria-label="Follow-up reminder" style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(17, 24, 39, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
    }}>
      <div style={{
        background: "white", borderRadius: 16, maxWidth: 440, width: "100%",
        padding: "1.75rem", boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
          {reminders.length > 1 ? `Reminder ${idx + 1} of ${reminders.length}` : "A reminder for you"}
        </div>
        <h2 style={{ margin: "0 0 0.6rem", color: "#111827", fontSize: "1.15rem" }}>
          {card.title || "Follow-up check-in"}
        </h2>
        <p style={{ margin: "0 0 1.5rem", color: "#4B5563", fontSize: "0.95rem", lineHeight: 1.55 }}>
          {card.body || "It's time for a follow-up. Your Patient Experience Manager will be in touch."}
        </p>
        <button onClick={acknowledge} disabled={busy} style={{
          width: "100%", padding: "0.7rem 1rem", background: "#2563eb", color: "white",
          border: "none", borderRadius: 10, fontSize: "0.95rem", fontWeight: 700,
          cursor: busy ? "wait" : "pointer",
        }}>
          {busy ? "Saving…" : (card.cta || "Got it")}
        </button>
      </div>
    </div>
  );
}
